import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '../api/auth'

export interface User {
  id: string
  email: string
  fullName: string
  roles: string[]
  modules: string[]
  tenantId: string
}

interface AuthStore {
  token: string | null
  refreshToken: string | null
  user: User | null
  isAuthenticated: boolean
  // docs/plan-license-expiry-read-only.md — refreshed on every login/
  // refresh/activate response, never derived locally.
  licenseActive: boolean
  licenseExpiresAt: string | null

  setTokens: (token: string, refreshToken: string, licenseActive: boolean, licenseExpiresAt: string | null) => void
  setUser: (user: User) => void
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

/** Decode JWT payload without a library (no signature verification needed client-side) */
function decodeJwt(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return {}
  }
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      licenseActive: true,
      licenseExpiresAt: null,

      setTokens: (token, refreshToken, licenseActive, licenseExpiresAt) =>
        set({ token, refreshToken, licenseActive, licenseExpiresAt }),
      setUser: (user) => set({ user, isAuthenticated: true }),

      login: async (email: string, password: string) => {
        const { data } = await authApi.login({ email, password })

        const payload = decodeJwt(data.access_token)

        const user: User = {
          id:       String(payload.sub ?? ''),
          email:    String(payload.email ?? email),
          fullName: String(payload.full_name ?? email.split('@')[0]),
          roles:    Array.isArray(payload.roles)   ? (payload.roles   as string[]) : [],
          modules:  Array.isArray(payload.modules) ? (payload.modules as string[]) : [],
          tenantId: String(payload.tenant_id ?? ''),
        }

        set({
          token:              data.access_token,
          refreshToken:       data.refresh_token,
          user,
          isAuthenticated:    true,
          licenseActive:      data.license_active,
          licenseExpiresAt:   data.license_expires_at,
        })
      },

      logout: () => set({ token: null, refreshToken: null, user: null, isAuthenticated: false }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token:            state.token,
        refreshToken:     state.refreshToken,
        user:             state.user,
        isAuthenticated:  state.isAuthenticated,
        licenseActive:    state.licenseActive,
        licenseExpiresAt: state.licenseExpiresAt,
      }),
    },
  ),
)
